// idea-vault/AddIdeaModal.tsx
import React, { useState, useEffect } from 'react';
import { X,   Clock, Users, Tag, FileText, CheckCircle, Sparkles,  Video, FileText as FileTextIcon, MessageSquare, Mic } from 'lucide-react';
import { Idea, CreateIdeaData, useIdeasStore } from '../../store/ideasStore';

interface AddIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  idea?: Idea | null;
}

const AddIdeaModal: React.FC<AddIdeaModalProps> = ({
  isOpen,
  onClose,
  idea
}) => {
  const { createIdea, updateIdea } = useIdeasStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<CreateIdeaData>({
    title: '',
    description: '',
    category: 'video',
    priority: 'medium',
    tags: [],
    estimatedEffort: 0,
    targetAudience: [],
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const categories = [
    { value: 'video', label: 'Video', icon: Video },
    { value: 'blog', label: 'Blog', icon: FileTextIcon },
    { value: 'social', label: 'Social', icon: MessageSquare },
    { value: 'podcast', label: 'Podcast', icon: Mic }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' }
  ];

  const targetAudienceOptions = [
    'developers', 'designers', 'students', 'beginners', 'professionals',
    'entrepreneurs', 'creators', 'gamers', 'tech-enthusiasts', 'general'
  ];

  // Initialize form with idea data if editing
  useEffect(() => {
    if (idea) {
      setFormData({
        title: idea.title || '',
        description: idea.description || '',
        category: idea.category || 'video',
        priority: idea.priority || 'medium',
        tags: idea.tags || [],
        estimatedEffort: idea.estimatedEffort || 0,
        targetAudience: idea.targetAudience || [],
        notes: idea.notes || '',
      });

      setTagInput(idea.tags?.join(', ') || '');
      setAudienceInput(idea.targetAudience?.join(', ') || '');
    }
  }, [idea]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = 'Idea title is required';
      if (!formData.description.trim()) newErrors.description = 'Description is required';
      if (formData.description.length < 10) newErrors.description = 'Description should be at least 10 characters';
    }

    if (step === 2) {
      if (!formData.category) newErrors.category = 'Category is required';
      if (formData.estimatedEffort <= 0) newErrors.estimatedEffort = 'Estimated effort must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const [tagInput, setTagInput] = useState(formData.tags?.join(", ") || "");
  const [audienceInput, setAudienceInput] = useState(formData.targetAudience?.join(", ") || "");

  const handleTagsChange = (value: string) => {
    setTagInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData(prev => ({
      ...prev,
      tags
    }));
  };

  const handleAudienceChange = (value: string) => {
    setAudienceInput(value);
    const audience = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      targetAudience: audience
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      setCurrentStep(1);
      return;
    }

    setIsSubmitting(true);

    try {
      if (idea) {
        await updateIdea({ ...formData, id: idea.id });
      } else {
        await createIdea(formData);
      }

      // Only close modal on success
      onClose();

      // Reset form
      setFormData({
        title: '',
        description: '',
        category: 'video',
        priority: 'medium',
        tags: [],
        estimatedEffort: 0,
        targetAudience: [],
        notes: '',
      });
      setCurrentStep(1);
      setErrors({});
      setTagInput('');
      setAudienceInput('');
    } catch (error) {
      console.error('Failed to save idea:', error);
      // Don't close modal on error - let user see the error and try again
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                {idea ? 'Edit Idea' : 'Add New Idea'}
              </h3>
              <p className="text-primary-100 text-sm mt-1">
                Step {currentStep} of 3
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 flex space-x-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-colors ${step <= currentStep ? 'bg-white' : 'bg-white/30'
                  }`}
              />
            ))}
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-primary-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">Idea Details</h4>
                <p className="text-gray-600 text-sm">Let's start with your creative idea</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Idea Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="e.g. 10 React Tips for Beginners"
                  />
                  {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Describe your idea in detail..."
                  />
                  {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Categorization */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Tag className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">Categorization</h4>
                <p className="text-gray-600 text-sm">Organize and prioritize your idea</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value as any)}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.category ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>{category.label}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-red-600 text-sm mt-1">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Level
                  </label>
                  <div className="flex gap-2">
                    {priorities.map(priority => (
                      <button
                        key={priority.value}
                        type="button"
                        onClick={() => handleInputChange('priority', priority.value)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${formData.priority === priority.value
                            ? priority.color + ' border-current'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}
                      >
                        {priority.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Effort (hours) *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.estimatedEffort}
                      onChange={(e) => handleInputChange('estimatedEffort', Number(e.target.value))}
                      className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.estimatedEffort ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="2.5"
                    />
                  </div>
                  {errors.estimatedEffort && <p className="text-red-600 text-sm mt-1">{errors.estimatedEffort}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => handleTagsChange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="react, tips, tutorial"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={audienceInput}
                      onChange={(e) => handleAudienceChange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="developers, beginners, students"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Common audiences: {targetAudienceOptions.join(', ')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Additional Notes */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">Additional Information</h4>
                <p className="text-gray-600 text-sm">Any extra details or notes</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Add any additional notes, research, or context for this idea..."
                />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-3">Idea Summary</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Title:</span>
                    <span className="ml-2 font-medium">{formData.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Category:</span>
                    <span className="ml-2 font-medium capitalize">{formData.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Priority:</span>
                    <span className="ml-2 font-medium capitalize">{formData.priority}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Effort:</span>
                    <span className="ml-2 font-medium">{formData.estimatedEffort}h</span>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="md:col-span-2">
                      <span className="text-gray-600">Tags:</span>
                      <span className="ml-2 font-medium">{formData.tags.join(', ')}</span>
                    </div>
                  )}
                  {formData.targetAudience.length > 0 && (
                    <div className="md:col-span-2">
                      <span className="text-gray-600">Audience:</span>
                      <span className="ml-2 font-medium">{formData.targetAudience.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CheckCircle className="w-4 h-4" />
            <span>All fields marked with * are required</span>
          </div>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Back
              </button>
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                disabled={isSubmitting}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {idea ? 'Update Idea' : 'Create Idea'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddIdeaModal;
